import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// MUI
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

// MUI Icons
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Custom Components
import FormContent from '@/components/FormContent';
import useAuthFormController from '@/components/useAuthFormController';
import { GridContainer, GridItem } from '@/ui/Grid';
import InfoBox from '@/ui/InfoBox';
import FormButton from '@/ui/Form/FormButton';
import SnackbarContext from '@/ui/SnackbarContext/SnackbarContext';
import model from '@/models/user';
import dataController from '@/lib/dataController';
import { sifrarnici } from '@/lib/sifrarnici';

// Services
import { authService } from '@/services/authService';

// Types
import { DCRecord } from '@/@types/lib/dataController';
import { RecordValidation } from '@/@types/lib/appValidator';
import { PickerItem } from '@/@types/controls/controls';
import { RegistrationFormProps } from '@/@types/views/Auth';

const validationKeysToOmit = ['hps_ime', 'hps_prezime', 'hps_email'];

function RegistrationForm(props: RegistrationFormProps) {
  const dc = new dataController(model);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const snackbarContext = useContext(SnackbarContext);

  const [hpsForm, setHpsForm] = useState(false);
  const [completed, setCompleted] = useState(false);

  const formId = hpsForm ? 'register_hps' : 'register';
  const { record, validation, fields, subModels, onFieldChange, doRegister, doValidate, setValidation } 
    = useAuthFormController({ 
    ...props, 
    form: formId 
  });

  const handleFieldChange = (value: any, source: string) => {
    if (onFieldChange) {
      onFieldChange(value, source);
    }
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleSubmit = () => {
    if (doRegister) {
      doRegister()
        .then((result) => {
          if (result.success) {
            setCompleted(true);
          } else if (result.canceled || !result.validationPass) {
            // nothing i guess?
          } else {
            if (result.errorCode !== 409) {
              // 409 already gives us form validation
              snackbarContext.showNotification('notifications.unexpected_error', 'error');
            }
            if (hpsForm) {
              handleGoBack();
            }
          }
        })
        .catch((resp) => {
          // cuz cancel is handeled as rejection??
          if (resp.canceled) {
            // nothing i guess?
          } else {
            if (resp.errorCode !== 409) {
              // 409 already gives us form validation
              snackbarContext.showNotification('notifications.unexpected_error', 'error');
            }
            if (hpsForm) {
              handleGoBack();
            }
          }
        });
    }
  };

  const clearHpsData = () => {
    const hpsFieldNames = dc.getFormFieldsNames('register_hps');

    hpsFieldNames.forEach((fieldName) => onFieldChange(null, fieldName));
  };

  // const checkHPSDateViaOIB = (oib: string): Promise<DataSingleResponse> => {
  //   return dc.GetDataSingle(oib, "hps/validate-vodic");
  // }

  const handleGoToHPSScren = () => {
    if (!doValidate()) {
      // TODO: Show a snackbar message for validation failure
      return;
    }

    const { oib } = record;
    if (typeof oib !== 'string' || oib.trim() === '') {
      console.error('Invalid OIB value');
      return;
    }

    authService
      .checkRegisterOIB(oib)
      .then((resp) => {
        if (!resp.success || !resp.data) {
          // console.error('Failed to fetch HPS data:', resp);
          snackbarContext.showNotification('notifications.non_hps_oib_extended', 'warning');
          setValidation({ oib: { valid: false, msg: t('notifications.non_hps_oib') } });
        } else if (resp.data) {
          const data = resp.data as DCRecord; // Type assertion
          Object.keys(data).forEach((key) => onFieldChange(data[key], key));
          setHpsForm(true);

          // eslint-disable-next-line @typescript-eslint/naming-convention
          const { hps_ime, hps_prezime, hps_email } = resp.data as DCRecord;
          // eslint-disable-next-line @typescript-eslint/naming-convention
          const { first_name, last_name, email } = record;

          const newValidation: RecordValidation = {};

          if (hps_ime !== first_name) {
            newValidation.hps_ime = { valid: false, msg: t('validation.hps_ime') };
          }

          if (hps_prezime !== last_name) {
            newValidation.hps_prezime = { valid: false, msg: t('validation.hps_prezime') };
          }

          if (hps_email !== email) {
            newValidation.hps_email = { valid: false, msg: t('validation.hps_email') };
          }

          setValidation(newValidation);
        }
      })
      .catch((e) => {
        console.error('Failed to fetch HPS data:', e);
        snackbarContext.showNotification('notifications.non_hps_oib', 'warning');
      });
  };

  const handleGoBack = () => {
    clearHpsData();
    setHpsForm(false);
    setValidation((prev) => Object.fromEntries(Object.entries(prev).filter(([key]) => !validationKeysToOmit.includes(key))));
  };

  const backHome = () => {
    navigate('/');
  };

  const canGoToHpsForm = useMemo(() => {
    return record.svrha_registracije && (record.svrha_registracije as PickerItem).value === sifrarnici.svrha_registracije.hps && !hpsForm;
  }, [record.svrha_registracije, hpsForm]);

  const hasValidationErrors = Object.keys(validation).length > 0;

  // const baseFields = useMemo(() => dc.getFormFields(hpsForm ? 'register_hps' : 'register'), [dc, hpsForm]);
  // const fieldsWithDynamicRequired = useMemo(() => {
  //   return baseFields.map(field => {
  //     if (field.source === 'oib' || field.source === 'mobitel') {
  //       return {
  //         ...field,
  //         validation: { ...field.validation, required: canGoToHpsForm },
  //       };
  //     }
  //     return field;
  //   });
  // }, [baseFields, canGoToHpsForm]);
  
  

  return (
    <Card>
      <CardHeader
        title={t('titles.registration')}
        action={
          hpsForm ? (
            <Tooltip color={hasValidationErrors ? 'error' : undefined} title={t('buttons.go_back')}>
              <IconButton size="small" onClick={handleGoBack}>
                <ArrowBackIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title={t('buttons.go_home')}>
              <IconButton size="small" onClick={backHome}>
                <CloseIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
          )
        }
      />
      <CardContent>
        <form autoComplete="off">
          <GridContainer>
            {completed ? (
              <GridItem size={12}>{t('messages.register.request.ok')}</GridItem>
            ) : hpsForm ? (
              <FormContent
                fieldNames={dc.getFormFieldsNames('register_hps')}
                record={record}
                validation={validation}
                fields={fields}
                onFieldChange={handleFieldChange}
                mode="insert"
                columns={2}
                subModels={subModels}
              />
            ) : (
              <FormContent
                fieldNames={dc.getFormFieldsNames('register')}
                record={record}
                validation={validation}
                fields={fields}
                onFieldChange={handleFieldChange}
                mode="insert"
                columns={2}
              />
            )}
            { hpsForm ?
            <GridItem size={12}>
              <InfoBox labelToken="messages.register.info_hps" />
            </GridItem> : null}
          </GridContainer>
        </form>
      </CardContent>
      <CardActions sx={{ flexDirection: { xs: 'column', md: 'row' }, rowGap: 1 }}>
        {completed ? (
          <FormButton variant="text" onClick={backHome} sx={{ mx: 1, width: { xs: '100%', md: 'auto' } }}>
            {t('buttons.back_home')}
          </FormButton>
        ) : (
          <FormButton
            disabled={hpsForm && hasValidationErrors}
            variant="contained"
            onClick={canGoToHpsForm ? handleGoToHPSScren : handleSubmit}
            sx={{ mx: 1, width: { xs: '100%', md: 'auto' } }}
          >
            {t(canGoToHpsForm ? 'buttons.next' : 'buttons.register')}
          </FormButton>
        )}
        {hpsForm && hasValidationErrors ? (
          <FormButton variant="contained" onClick={handleGoBack} sx={{ mx: 1, width: { xs: '100%', md: 'auto' } }}>
            {t('buttons.go_back_small')}
          </FormButton>
        ) : null}
        {!completed ? (
          <FormButton variant="text" onClick={handleLogin} sx={{ mx: 1, width: { xs: '100%', md: 'auto' } }}>
            {t('buttons.cancel')}
          </FormButton>
        ) : null}
      </CardActions>
    </Card>
  );
}

export default RegistrationForm;
